import { LitElement, TemplateResult, html } from "lit";
import {
  customElement,
  property,
  query,
  queryAssignedNodes,
  state,
} from "lit/decorators.js";

import {
  Sizes,
  SizeResolverService as sizeResolver,
} from "@slhuicore/utils/size.js";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { style } from "./assets/sidebar-navigation.generated-style";
import { ICON_HEIGHT, ICON_WIDTH } from "./sidebar-navigation.constants";

/**
 * @CustomElement slh-sidebar-navigation
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-xs-font-size - Font size.
 * @cssprop --slh-base-bold-font-weight - Font weight.
 * @cssprop --slh-base-xxs-line-height - Line height.
 * @cssprop --slh-color-neutral-shade-00 - Background color.
 * @cssprop --slh-color-neutral-shade-20 - Bottom border color.
 * @cssprop --slh-color-neutral-shade-60 - Heading icon color.
 * @cssprop --slh-color-neutral - Heading font color.
 *
 * @slot default - list of slh-sidebar-navigation-item
 * @slot heading - descriptive heading.
 */
@customElement("slh-sidebar-navigation")
export default class SlhSidebarNavigation extends LitElement {
  @queryAssignedNodes()
  protected itemsSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "heading" })
  protected headingSlotNodes: Array<HTMLElement> | undefined;

  @query(".heading", false)
  private headingElement!: HTMLElement;

  @query(".sidebar-holder", false)
  private sidebarHolderElement!: HTMLElement;

  @property({
    type: String,
    reflect: true,
  })
  heading: String = "";

  @state()
  openedSidebar: boolean = false;

  private onResize: (e: Event) => void = () => {};

  static styles = style;

  connectedCallback() {
    super.connectedCallback();
    this.onResize = this.checkMobileView.bind(this);
    window.addEventListener("resize", this.onResize);
    this.classList.add("slh-sidebar-navigation-wrapper");
    this.checkMobileView();
  }

  protected firstUpdated(): void {
    this.headingElement.addEventListener(
      "keydown",
      this.handleKeydown.bind(this)
    );
    this.checkEmptyItems();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    this.headingElement.removeEventListener(
      "keydown",
      this.handleKeydown.bind(this)
    );

    super.disconnectedCallback();
  }

  /**
   * Check mobile view
   */
  private checkMobileView() {
    sizeResolver.checkWindowSize();
    if (sizeResolver.getSize() <= Sizes.MD) {
      this.classList.add("mobile");
    } else {
      this.classList.remove("mobile");
    }
  }

  /**
   * @param {KeyboardEvent} event
   * w3.org keyboard support
   */
  protected handleKeydown(event: KeyboardEvent) {
    event.stopPropagation();
    const { code } = event;

    if (code === "ArrowDown") {
      this.openSidebar();
    } else if (code === "ArrowUp") {
      this.closeSidebar();
    } else if (code === "Enter" || code === "NumpadEnter") {
      this.toggleSidebar(event);
    } else if (code === "Space") {
      this.toggleSidebar(event);
    }
    return true;
  }

  /**
   * toggle sidebar
   */
  toggleSidebar(event: Event) {
    event.preventDefault();
    if (this.openedSidebar) {
      this.closeSidebar();
    } else {
      this.openSidebar();
    }
  }

  closeSidebar(): void {
    this.openedSidebar = false;
    this.classList.remove("opened");
  }

  openSidebar(): void {
    this.openedSidebar = true;
    this.classList.add("opened");
  }

  // eslint-disable-next-line class-methods-use-this
  render(): TemplateResult {
    return html`<div class="sidebar-holder">
      <div
        class="heading"
        role="button"
        tabindex="0"
        aria-expanded="${this.openedSidebar}"
        aria-label="${this.heading}"
        @click="${this.toggleSidebar}"
      >
        ${this.heading}
        <slh-icon
          type="${this.openedSidebar
            ? IconType.ICO_CLOSE
            : IconType.ICO_ARROW_OPEN_DOWN}"
          width="${ICON_WIDTH}"
          height="${ICON_HEIGHT}"
        ></slh-icon>
      </div>
      <slot name="heading"></slot>
      <div class="items-holder" role="navigation">
        <slot @slotchange="${this.onSlotChange}"></slot>
      </div>
    </div>`;
  }

  protected onSlotChange(): void {
    this.checkEmptyItems();
  }

  protected checkEmptyItems(): void {
    let empty: boolean = true;
    this.itemsSlotNodes?.forEach((node: HTMLElement) => {
      if (node.nodeName.endsWith("SIDEBAR-NAVIGATION-ITEM")) {
        empty = false;
      }
    });
    if (empty) {
      this.sidebarHolderElement.classList.add("empty");
    } else {
      this.sidebarHolderElement.classList.remove("empty");
    }
  }

  public appendHeading(htmlElement: HTMLElement): void {
    const clone = htmlElement.cloneNode(true) as HTMLElement;
    clone.style.padding = "0";

    this.headingElement.prepend(clone);
    if (this.heading) {
      this.heading = "";
    }
  }
}
